import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { Lesson, Project, Sub } from '../types'
import { fmtDate } from '../lib/format'
import Badge, { type BadgeColor } from '../components/Badge'

const CATEGORIES: Array<{ id: Lesson['category']; label: string; color: BadgeColor }> = [
  { id: 'went_well', label: 'Went well', color: 'green' },
  { id: 'went_wrong', label: 'Went wrong', color: 'red' },
  { id: 'change_next_time', label: 'Change next time', color: 'gold' },
  { id: 'spec_book_change', label: 'Spec Book change', color: 'gold' },
  { id: 'sub_note', label: 'Sub note', color: 'blue' },
  { id: 'process_note', label: 'Process note', color: 'blue' },
  { id: 'observation', label: 'Observation', color: 'gray' },
]

export default function LessonsTab({ project }: { project: Project }) {
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [subs, setSubs] = useState<Sub[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<Lesson>>({ category: 'observation' })
  const [filter, setFilter] = useState<Lesson['category'] | 'all'>('all')

  useEffect(() => {
    void api.lessons.list(project.id).then(setLessons)
    void api.subs.list().then(setSubs)
  }, [project.id])

  const filtered = useMemo(() => {
    if (filter === 'all') return lessons
    return lessons.filter((l) => l.category === filter)
  }, [lessons, filter])

  async function add() {
    if (!draft.title || !draft.body || !draft.category) return
    const l = await api.lessons.create(project.id, draft)
    setLessons((p) => [l, ...p])
    setAdding(false)
    setDraft({ category: 'observation' })
  }

  async function toggleApplied(l: Lesson) {
    const updated = await api.lessons.update(l.id, { applied: l.applied ? 0 : 1 })
    setLessons((p) => p.map((x) => (x.id === l.id ? updated : x)))
  }

  async function remove(id: string) {
    if (!window.confirm('Delete this lesson?')) return
    await api.lessons.delete(id)
    setLessons((p) => p.filter((x) => x.id !== id))
  }

  const [generating, setGenerating] = useState(false)
  const [eobReport, setEobReport] = useState<Awaited<
    ReturnType<typeof api.endOfBuild.generate>
  > | null>(null)
  async function generateLessons() {
    if (
      !window.confirm(
        'Run end-of-build extraction? Pulls every log/walk/punch/CO/rating and asks Claude to produce lessons. Persists each as a Lesson row.'
      )
    )
      return
    setGenerating(true)
    try {
      const r = await api.endOfBuild.generate(project.id, true)
      setEobReport(r)
      setLessons(await api.lessons.list(project.id))
    } catch (e) {
      alert((e as Error).message)
    } finally {
      setGenerating(false)
    }
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Lessons</h2>
          <p className="muted small">
            {lessons.length} captured · post-build learnings, sub notes, spec changes, process
            improvements. The reason build #5 is meaningfully better than build #1.
          </p>
        </div>
        <div className="row gap-2">
          <select className="search" value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)}>
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <button className="btn-ghost" onClick={generateLessons} disabled={generating}>
            {generating ? 'Generating…' : 'Generate end-of-build lessons'}
          </button>
          <button className="btn-primary" onClick={() => setAdding(!adding)}>
            {adding ? 'Cancel' : '+ Lesson'}
          </button>
        </div>
      </div>

      {eobReport && (
        <div className="card mb-3" style={{ background: 'rgba(46,160,67,0.06)' }}>
          <h3 style={{ marginTop: 0 }}>End-of-build report</h3>
          {eobReport.summary && <p>{eobReport.summary}</p>}
          {eobReport.lessons_inserted != null && (
            <p className="muted small">
              {eobReport.lessons_inserted} lessons added below.
            </p>
          )}
          {eobReport.sub_recommendations && eobReport.sub_recommendations.length > 0 && (
            <details>
              <summary className="muted small" style={{ cursor: 'pointer' }}>
                Sub recommendations ({eobReport.sub_recommendations.length})
              </summary>
              <ul>
                {eobReport.sub_recommendations.map((s, i) => (
                  <li key={i} className="small">
                    <strong>{s.sub_name}</strong> — {s.verdict}: {s.rationale}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {eobReport.spec_book_recommendations && eobReport.spec_book_recommendations.length > 0 && (
            <details>
              <summary className="muted small" style={{ cursor: 'pointer' }}>
                Spec Book recommendations ({eobReport.spec_book_recommendations.length})
              </summary>
              <ul>
                {eobReport.spec_book_recommendations.map((s, i) => (
                  <li key={i} className="small">
                    {s}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {eobReport.process_improvements && eobReport.process_improvements.length > 0 && (
            <details>
              <summary className="muted small" style={{ cursor: 'pointer' }}>
                Process improvements ({eobReport.process_improvements.length})
              </summary>
              <ul>
                {eobReport.process_improvements.map((s, i) => (
                  <li key={i} className="small">
                    {s}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Category</span>
            <select
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value as Lesson['category'] })}
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="form-row">
            <span>Trade</span>
            <input
              value={draft.trade ?? ''}
              onChange={(e) => setDraft({ ...draft, trade: e.target.value })}
              placeholder="framing, plumbing… (optional)"
            />
          </label>
          <label className="form-row">
            <span>Phase</span>
            <input
              value={draft.phase ?? ''}
              onChange={(e) => setDraft({ ...draft, phase: e.target.value })}
              placeholder="Foundation & Slab… (optional)"
            />
          </label>
          <label className="form-row">
            <span>Related sub</span>
            <select
              value={draft.related_sub_id ?? ''}
              onChange={(e) => setDraft({ ...draft, related_sub_id: e.target.value || null })}
            >
              <option value="">— none —</option>
              {subs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.company_name}
                </option>
              ))}
            </select>
          </label>
          <label className="form-row">
            <span>Title *</span>
            <input
              value={draft.title ?? ''}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Short summary"
            />
          </label>
          <label className="form-row">
            <span>Body *</span>
            <textarea
              rows={4}
              value={draft.body ?? ''}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              placeholder="What happened, what to do differently next time, why."
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Capture lesson
          </button>
        </div>
      )}

      {filtered.length === 0 && !adding && <p className="muted">No lessons in this view.</p>}

      {filtered.map((l) => {
        const cat = CATEGORIES.find((c) => c.id === l.category)
        return (
          <div key={l.id} className="card mb-2">
            <div className="row-between">
              <div className="grow">
                <div className="row-title">{l.title}</div>
                <div className="muted small">
                  {fmtDate(l.created_at)}
                  {l.trade && ` · ${l.trade}`}
                  {l.phase && ` · ${l.phase}`}
                  {l.related_sub_name && ` · ${l.related_sub_name}`}
                </div>
                <div className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                  {l.body}
                </div>
                {l.applied === 1 && l.applied_notes && (
                  <div className="muted small mt-2">
                    <strong>Applied:</strong> {l.applied_notes}
                  </div>
                )}
              </div>
              <div className="text-right">
                <Badge color={cat?.color ?? 'gray'}>{cat?.label.toUpperCase() ?? l.category}</Badge>
                {l.applied === 1 && (
                  <div className="mt-1">
                    <Badge color="green">APPLIED</Badge>
                  </div>
                )}
              </div>
            </div>
            <div className="row gap-2 mt-2">
              <button className="btn-ghost small" onClick={() => toggleApplied(l)}>
                {l.applied ? 'Unmark applied' : 'Mark applied'}
              </button>
              <button className="btn-ghost small" onClick={() => remove(l.id)}>
                ✕ Delete
              </button>
            </div>
          </div>
        )
      })}
    </section>
  )
}
